import React, { useState, useEffect } from "react";
import axios from "axios";
import axiosInstance from "../../../axiosInstance";
import { Link } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import toast from "react-hot-toast";
import Nestable from "react-nestable";
import { triggerChooseimg } from "../../components/ChooseImg";
import Gallery from "../../components/Gallery";
import { Helmet } from "react-helmet";
import $ from "jquery";

const PUBLIC_HOME_FIELD_GROUPS = [
  {
    title: "Background Images",
    help: "Add or remove the background images available for the home page.",
    fields: [],
  },
  {
    title: "Hero Section",
    help: "Controls the first screen, email CTA, store buttons, and phone image.",
    fields: [
      { name: "hero_kicker", label: "Hero Kicker" },
      { name: "hero_title", label: "Hero Title" },
      { name: "hero_highlight_prefix", label: "Hero Highlight Prefix" },
      { name: "hero_highlight", label: "Hero Highlight" },
      { name: "hero_description", label: "Hero Description", textarea: true },
      { name: "hero_features", label: "Hero Features (one per line)", textarea: true },
      { name: "hero_email_placeholder", label: "Hero Email Placeholder" },
      { name: "hero_alert_time", label: "Hero Floating Alert Time" },
      { name: "app_screen_image", label: "Hero App Screen Image URL" },
    ],
  },
  {
    title: "Store Buttons",
    help: "Used in the hero and app/profile sections.",
    fields: [
      { name: "google_play_url", label: "Google Play Store Link" },
      { name: "google_play_label", label: "Google Play Small Label" },
      { name: "google_play_title", label: "Google Play Main Label" },
      { name: "app_store_url", label: "App Store Link" },
      { name: "app_store_label", label: "App Store Small Label" },
      { name: "app_store_title", label: "App Store Main Label" },
    ],
  },
  {
    title: "Featured Jobs",
    help: "Controls the recommended jobs block below the hero.",
    fields: [
      { name: "featured_jobs_heading", label: "Featured Jobs Heading" },
      { name: "featured_jobs_subheading", label: "Featured Jobs Subheading" },
      { name: "featured_jobs_button_text", label: "Featured Jobs Button Text" },
      { name: "featured_jobs_button_link", label: "Featured Jobs Button Link" },
      { name: "featured_jobs_empty_text", label: "Featured Jobs Empty Text" },
      { name: "featured_jobs_apply_text", label: "Job Card Apply Button Text" },
      { name: "featured_jobs_posted_text", label: "Job Card Posted Text" },
    ],
  },
  {
    title: "Marquee Logos",
    help: "Manage the scrolling company logo marquee shown above Featured Job Circulars.",
    fields: [],
  },
  {
    title: "Stats And Download Banner",
    help: "Manage homepage stats and the app download banner.",
    fields: [
      { name: "download_app_heading", label: "Download App Heading" },
      { name: "download_app_title", label: "Download App Title" },
      { name: "download_app_description", label: "Download App Description", textarea: true },
      { name: "download_app_features", label: "Download App Features (one per line)", textarea: true },
      { name: "qr_image", label: "QR Code Image URL" },
      { name: "qr_label", label: "QR Label" },
    ],
  },
  {
    title: "Job Categories",
    help: "Add the categories shown on the public home page.",
    fields: [],
  },
  {
    title: "How It Works And Testimonials",
    help: "How steps use: Icon | Title | Description | Badge Icon | Badge Text. Testimonials use: Name | Role | Company | Text | Rating.",
    fields: [
      { name: "how_kicker", label: "How It Works Kicker" },
      { name: "how_heading", label: "How It Works Heading" },
      { name: "how_description", label: "How It Works Description", textarea: true },
      { name: "testimonial_kicker", label: "Testimonial Kicker" },
      { name: "testimonial_heading", label: "Testimonial Heading" },
      { name: "testimonial_highlight", label: "Testimonial Highlight" },
    ],
  },
  {
    title: "Profile App Section",
    help: "Controls the lower app/profile CTA and its image.",
    fields: [
      { name: "profile_section_heading", label: "Profile Section Heading" },
      { name: "profile_section_highlight", label: "Profile Section Highlight" },
      { name: "profile_section_description", label: "Profile Section Description", textarea: true },
      { name: "profile_section_features", label: "Profile Section Features (one per line)", textarea: true },
      { name: "profile_section_image", label: "Profile Section Image URL" },
      { name: "profile_email_placeholder", label: "Profile Email Placeholder" },
      { name: "profile_button_text", label: "Profile Button Text" },
    ],
  },
  {
    title: "FAQ And Blog",
    help: "Manage FAQ rows and latest blog section text.",
    fields: [
      { name: "faq_kicker", label: "FAQ Kicker" },
      { name: "faq_heading", label: "FAQ Heading" },
      { name: "faq_description", label: "FAQ Description", textarea: true },
      { name: "faq_button_text", label: "FAQ Button Text" },
      { name: "faq_button_link", label: "FAQ Button Link" },
      { name: "blog_kicker", label: "Blog Kicker" },
      { name: "blog_heading", label: "Blog Heading" },
      { name: "blog_button_text", label: "Blog Button Text" },
      { name: "blog_button_link", label: "Blog Button Link" },
      { name: "blog_category_label", label: "Blog Card Category Label" },
      { name: "blog_description", label: "Blog Card Description", textarea: true },
      { name: "blog_read_text", label: "Blog Card Read Link Text" },
    ],
  },
];

const PUBLIC_HOME_FIELDS = PUBLIC_HOME_FIELD_GROUPS.flatMap((group) => group.fields);

const splitLines = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const parsePipeItems = (value, mapper, fallback) => {
  const parsed = splitLines(value)
    .map((line, index) => mapper(line.split("|").map((part) => part.trim()), index))
    .filter(Boolean);
  return parsed.length ? parsed : fallback;
};

const serializeCategoryItems = (items) =>
  items
    .filter((item) => item.name?.trim())
    .map((item) =>
      [item.icon, item.name, item.description, item.badge, item.link]
        .map((value) => String(value || "").trim())
        .join(" | ")
    )
    .join("\n");

const serializeStatsItems = (items) =>
  items
    .filter((item) => item.value?.trim() || item.label?.trim())
    .map((item) =>
      [item.icon, item.value, item.label]
        .map((value) => String(value || "").trim())
        .join(" | ")
    )
    .join("\n");

const serializeHowStepItems = (items) =>
  items
    .filter((item) => item.title?.trim() || item.description?.trim())
    .map((item) =>
      [item.icon, item.title, item.description, item.badgeIcon, item.badgeText]
        .map((value) => String(value || "").trim())
        .join(" | ")
    )
    .join("\n");

const serializeTestimonialItems = (items) =>
  items
    .filter((item) => item.name?.trim() || item.text?.trim())
    .map((item) =>
      [item.name, item.role, item.company, item.text, item.rating]
        .map((value) => String(value || "").trim())
        .join(" | ")
    )
    .join("\n");

const serializeFaqItems = (items) =>
  items
    .filter((item) => item.question?.trim() || item.answer?.trim())
    .map((item) =>
      [item.question, item.answer]
        .map((value) => String(value || "").trim())
        .join(" | ")
    )
    .join("\n");

const defaultStatsItems = () => [
  { icon: "ri-briefcase-4-line", value: "45,000+", label: "Jobs Available" },
  { icon: "ri-building-4-line", value: "5,000+", label: "Companies" },
  { icon: "ri-team-line", value: "1,500+", label: "Recruiters" },
  { icon: "ri-award-line", value: "98%", label: "Success Rate" },
];

const emptyCategoryItem = () => ({
  icon: "ri-briefcase-line",
  name: "",
  description: "",
  badge: "",
  link: "/jobs",
});

const emptyStatsItem = () => ({
  icon: "ri-bar-chart-box-line",
  value: "",
  label: "",
});

const emptyHowStepItem = () => ({
  icon: "ri-user-add-line",
  title: "",
  description: "",
  badgeIcon: "ri-time-line",
  badgeText: "",
});

const emptyTestimonialItem = () => ({
  name: "",
  role: "",
  company: "",
  text: "",
  rating: "5",
});

const emptyFaqItem = () => ({
  question: "",
  answer: "",
});

const emptyLogoMarqueeItem = () => ({
  imageInput: "",
  imageUrlInput: "",
  title: "",
});

const emptyBackgroundImageItem = () => ({
  imageInput: "",
  title: "",
});

const HomeLayout = () => {
  const [loading, setLoading] = useState(true); // Add loading state
  const [Products, setProducts] = useState([]); // Add loading state

  const getProducts = async () => {
    try {
      const { data } = await axiosInstance.get("/all-products");
      console.log("attr", data);
      setProducts(data.products);
    } catch (error) {
      console.error("Error fetching attributes:", error);
    }
  };

  useEffect(() => {
    getProducts();
  }, []);

  const [formData, setFormData] = useState({
    home_slider: "",
    trending_product: "",
    trending_product_banner: "",
    trending_product_carousal: "",
    best_selling_laptop: "",
    collection_heading: "",
    collection_paragraph: "",
    collection_url: "",
    collection_img: "",
    latest_product: "",
    latest_product_banner: "",
    latest_product_carousal: "",
    best_selling_smartphone: "",
    top_bar: "",
    category_items_text: "",
    how_steps_text: "",
    testimonial_items_text: "",
    ...Object.fromEntries(PUBLIC_HOME_FIELDS.map((field) => [field.name, ""])),
  });

  const [statsItems, setStatsItems] = useState(defaultStatsItems());
  const [categoryItems, setCategoryItems] = useState([emptyCategoryItem()]);
  const [howStepItems, setHowStepItems] = useState([emptyHowStepItem()]);
  const [testimonialItems, setTestimonialItems] = useState([emptyTestimonialItem()]);
  const [faqItems, setFaqItems] = useState([emptyFaqItem()]);
  const [logoMarqueeItems, setLogoMarqueeItems] = useState([emptyLogoMarqueeItem()]);
  const [backgroundImageItems, setBackgroundImageItems] = useState([emptyBackgroundImageItem()]);

  const [imageSliderDivs, setImageSliderDivs] = useState([]);

  const addSliderImageDiv = () => {
    const newImageDiv = (
      <div key={imageSliderDivs.length} className={`col-md-4 col-6`}>
        <div className={`input-group imageupload-bx mb-3`}>
          <img
            className="image-preview"
            src="/assets/images/placeholder.png"
            alt="Preview"
            id={`imageinputSlider${imageSliderDivs.length}`}
            data-bs-toggle="modal"
            data-bs-target="#modal-default"
          />
          <input
            type="hidden"
            className="form-control imageopen"
            id={`imageinputSlider1${imageSliderDivs.length}`}
            name="productSliderImg[]"
            value="/assets/images/placeholder.png"
          />
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageSliderDivs, newImageDiv];
    setImageSliderDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for trending product banner

  const [imageTbannerDivs, setImageTbannerDivs] = useState([]);

  const addTbannerImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageTbannerDivs.length} className={`col-md-3 col-6`}>
        <div className="imageTbannerDivs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageinputTbannerImg${imageTbannerDivs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageinputTbanner${imageTbannerDivs.length}`}
              name="productTbannerImg[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Category Name{" "}
            </label>
            <input
              type="text"
              className="form-control productTbannerTIT"
              id={`productTbannerTIT${imageTbannerDivs.length}`}
              name="productTbannerTIT[]"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control productTbannerUrl"
              id={`productTbannerUrl${imageTbannerDivs.length}`}
              name="productTbannerUrl[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>

        <br />
      </div>
    );

    const updatedImageDivs = [...imageTbannerDivs, newTbannerImageDiv];
    setImageTbannerDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for latest product banner

  const [imageTbanner2Divs, setImageTbanner2Divs] = useState([]);

  const addTbanner2ImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageTbanner2Divs.length} className={`col-md-3 col-6`}>
        <div className="imageTbanner2Divs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageinputTbanner2${imageTbanner2Divs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageinputTbanner2Url${imageTbanner2Divs.length}`}
              name="imageinputTbanner2Url[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Title
            </label>
            <input
              type="text"
              className="form-control productTbanner2TIT"
              id={`productTbanner2TIT${imageTbanner2Divs.length}`}
              name="productTbanner2TIT[]"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Paragraph
            </label>
            <input
              type="text"
              className="form-control productTbanner2Para"
              id={`productTbanner2Para${imageTbanner2Divs.length}`}
              name="productTbanner2Para[]"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control productTbanner2Url"
              id={`productTbanner2Url${imageTbanner2Divs.length}`}
              name="productTbanner2Url[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>

        <br />
      </div>
    );

    const updatedImageDivs = [...imageTbanner2Divs, newTbannerImageDiv];
    setImageTbanner2Divs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for trending product carousal

  const [imageTcarousalDivs, setImageTcarousalDivs] = useState([]);

  const addTcarousalImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageTcarousalDivs.length} className={`col-md-3 col-6`}>
        <div className="imageTcarousalDivs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageTcarousalinputS${imageTcarousalDivs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageTcarousalinputSImg${imageTcarousalDivs.length}`}
              name="productTcarousalImg[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control imageTcarousalDivs"
              id={`productTcarousalUrl${imageTcarousalDivs.length}`}
              name="productTcarousalUrl[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageTcarousalDivs, newTbannerImageDiv];
    setImageTcarousalDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for normal  banner

  const [imageNcarousalDivs, setImageNcarousalDivs] = useState([]);

  const addNcarousalImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageNcarousalDivs.length} className={`col-md-3 col-6`}>
        <div className="imageTcarousalDivs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageNcarousalinputS${imageNcarousalDivs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageTcarousalinputSImg${imageNcarousalDivs.length}`}
              name="productNcarousalImg[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control imageTcarousalDivs"
              id={`productNcarousalUrl${imageNcarousalDivs.length}`}
              name="productNcarousalUrl[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageNcarousalDivs, newTbannerImageDiv];
    setImageNcarousalDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for normal  Logos

  const [imageLcarousalDivs, setImageLcarousalDivs] = useState([]);

  const addLcarousalImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageLcarousalDivs.length} className={`col-md-3 col-6`}>
        <div className="imageTcarousalDivs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageLcarousalinputS${imageLcarousalDivs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageLcarousalinputSImg${imageLcarousalDivs.length}`}
              name="productTcarousalImg[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control imageTcarousalDivs"
              id={`productLcarousalUrl${imageLcarousalDivs.length}`}
              name="productTcarousalUrl[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageLcarousalDivs, newTbannerImageDiv];
    setImageLcarousalDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for normal  Logos

  const [imageBBcarousalDivs, setImageBBcarousalDivs] = useState([]);

  const addBBcarousalImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageBBcarousalDivs.length} className={`col-md-3 col-6`}>
        <div className="imageTcarousalDivs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageBBcarousalinputS${imageBBcarousalDivs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageBBcarousalinputSImg${imageBBcarousalDivs.length}`}
              name="productBBcarousalImg[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control imageTcarousalDivs"
              id={`productBBcarousalUrl${imageBBcarousalDivs.length}`}
              name="productTcarousalUrl[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageBBcarousalDivs, newTbannerImageDiv];
    setImageBBcarousalDivs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  // for trending two product carousal

  const [imageTcarousal2Divs, setImageTcarousal2Divs] = useState([]);

  const addTcarousal2ImageDiv = () => {
    const newTbannerImageDiv = (
      <div key={imageTcarousal2Divs.length} className={`col-md-3 col-6`}>
        <div className="imageTcarousal2Divs-box">
          <div className={`input-group imageupload-bx mb-3`}>
            <img
              className="image-preview"
              src="/assets/images/placeholder.png"
              alt="Preview"
              id={`imageTcarousal2inputSImg${imageTcarousal2Divs.length}`}
              data-bs-toggle="modal"
              data-bs-target="#modal-default"
            />
            <input
              type="hidden"
              className="form-control imageopen"
              id={`imageTcarousal2inputS${imageTcarousal2Divs.length}`}
              name="productTcarousal2Img[]"
              value="/assets/images/placeholder.png"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Title
            </label>
            <input
              type="text"
              className="form-control productTcarousal2TIT"
              id={`productTcarousal2TIT${imageTcarousal2Divs.length}`}
              name="productTcarousal2TIT[]"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="text-center d-block">
              Image Url
            </label>
            <input
              type="text"
              className="form-control productTcarousal2Url"
              id={`productTcarousal2Url${imageTcarousal2Divs.length}`}
              name="productTcarousal2Url[]"
            />
          </div>
        </div>
        <button
          className="btn btn-danger d-block m-auto"
          onClick={removeSliderImageDiv}
        >
          Delete
        </button>
        <br />
      </div>
    );

    const updatedImageDivs = [...imageTcarousal2Divs, newTbannerImageDiv];
    setImageTcarousal2Divs(updatedImageDivs);
    setTimeout(function () {
      triggerChooseimg();
    }, 1);
  };

  const removeSliderImageDiv = (event) => {
    const button = event.target;
    const parentDiv = button.parentNode; // Accessing the parent div
    parentDiv.remove(); // Remove the parent div
  };

  const [SubmitLoading, setSubmitLoading] = useState(true); // Add loading state

  const [layout, setLayout] = useState({});

  const [isLoading, setIsLoading] = useState(true);

  const getData = async () => {
    try {
      const { data } = await axiosInstance.get(`/home-layout-data`);
      setLayout(data.homeLayout);
      setIsLoading(false); // Set loading state to false in case of an error
      console.log("data", data);

      setTimeout(function () {
        triggerChooseimg();
      }, 1);

      setFormData((prevData) => ({
        ...prevData,

        collection_heading: data.homeLayout.collection_heading,
        collection_paragraph: data.homeLayout.collection_paragraph,
        collection_url: data.homeLayout.collection_url,
        top_bar: data.homeLayout.top_bar,
        stats_items_text: data.homeLayout.stats_items_text || "",
        category_items_text: data.homeLayout.category_items_text || "",
        how_steps_text: data.homeLayout.how_steps_text || "",
        testimonial_items_text: data.homeLayout.testimonial_items_text || "",
        faq_items_text: data.homeLayout.faq_items_text || "",
        ...Object.fromEntries(
          PUBLIC_HOME_FIELDS.map((field) => [field.name, data.homeLayout[field.name] || ""])
        ),
      }));

      setStatsItems(
        parsePipeItems(
          data.homeLayout.stats_items_text,
          ([icon, value, label]) =>
            value || label ? { icon: icon || "ri-bar-chart-box-line", value, label } : null,
          defaultStatsItems()
        )
      );
      setCategoryItems(
        parsePipeItems(
          data.homeLayout.category_items_text,
          ([icon, name, description, badge, link]) =>
            name ? { icon: icon || "ri-briefcase-line", name, description, badge, link } : null,
          [emptyCategoryItem()]
        )
      );
      setHowStepItems(
        parsePipeItems(
          data.homeLayout.how_steps_text,
          ([icon, title, description, badgeIcon, badgeText]) =>
            title || description
              ? { icon: icon || "ri-user-add-line", title, description, badgeIcon, badgeText }
              : null,
          [emptyHowStepItem()]
        )
      );
      setTestimonialItems(
        parsePipeItems(
          data.homeLayout.testimonial_items_text,
          ([name, role, company, text, rating]) =>
            name || text ? { name, role, company, text, rating: rating || "5" } : null,
          [emptyTestimonialItem()]
        )
      );
      setFaqItems(
        parsePipeItems(
          data.homeLayout.faq_items_text,
          ([question, answer]) =>
            question || answer ? { question, answer } : null,
          [emptyFaqItem()]
        )
      );
      setLogoMarqueeItems(
        Array.isArray(data.homeLayout.service_logos) && data.homeLayout.service_logos.length
          ? data.homeLayout.service_logos.map((item) => ({
              imageInput: item?.imageInput || item?.image || item?.src || "",
              imageUrlInput: item?.imageUrlInput || item?.link || item?.href || "",
              title: item?.title || item?.name || "",
            }))
          : [emptyLogoMarqueeItem()]
      );
      setBackgroundImageItems(
        Array.isArray(data.homeLayout.background_images) && data.homeLayout.background_images.length
          ? data.homeLayout.background_images.map((item) => ({
              imageInput: item?.imageInput || item?.image || item?.src || "",
              title: item?.title || item?.name || "",
            }))
          : [emptyBackgroundImageItem()]
      );
    } catch (error) {
      console.log(error);
      toast.error("Error fetching Home layout!");
      setIsLoading(false); // Set loading state to false in case of an error
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateArrayItem = (setter, index, field, value) => {
    setter((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const removeArrayItem = (setter, index, emptyFactory) => {
    setter((items) => {
      const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
      return nextItems.length ? nextItems : [emptyFactory()];
    });
  };

  const submitData = async () => {
    setSubmitLoading(false);

    const updatedFormData = {
      ...layout,
      ...formData,
      stats_items_text: serializeStatsItems(statsItems),
      category_items_text: serializeCategoryItems(categoryItems),
      how_steps_text: serializeHowStepItems(howStepItems),
      testimonial_items_text: serializeTestimonialItems(testimonialItems),
      faq_items_text: serializeFaqItems(faqItems),
      service_logos: logoMarqueeItems
        .filter((item) => String(item.imageInput || "").trim())
        .map((item) => ({
          imageInput: String(item.imageInput || "").trim(),
          imageUrlInput: String(item.imageUrlInput || "").trim(),
          title: String(item.title || "").trim(),
        })),
      background_images: backgroundImageItems
        .filter((item) => String(item.imageInput || "").trim())
        .map((item) => ({
          imageInput: String(item.imageInput || "").trim(),
          title: String(item.title || "").trim(),
        })),
    };

    console.log(updatedFormData);

    try {
      await axiosInstance.put(`/admin/edit-home-layout`, updatedFormData);
      toast.success("Home Layout Updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Error On Menu:", error);
      toast.error(error.response.data.message);
    } finally {
      setSubmitLoading(true);
    }
  };

  return (
    <>
      {!isLoading && (
        <>
          <Helmet>
            <link
              href="https://raw.githack.com/ttskch/select2-bootstrap4-theme/master/dist/select2-bootstrap4.css"
              rel="stylesheet"
            />
            <link
              href="https://cdnjs.cloudflare.com/ajax/libs/select2/4.0.6-rc.0/css/select2.min.css"
              rel="stylesheet"
            />
            <script src="https://cdnjs.cloudflare.com/ajax/libs/select2/4.0.6-rc.0/js/select2.min.js">
              {" "}
            </script>

            <script>
              {`
    $(function () {
      $('select').each(function () {
        $(this).select2({
          theme: 'bootstrap4',
          width: 'style',
          placeholder: $(this).attr('placeholder'),
          allowClear: Boolean($(this).data('allow-clear')),
        });
      })
    });
    `}
            </script>
          </Helmet>
        </>
      )}

      <Sidebar />
      <main className="content">
        <Navbar />

        <nav aria-label="breadcrumb" className="d-none d-md-inline-block">
          <ol className="breadcrumb breadcrumb-dark breadcrumb-transparent">
            <li className="breadcrumb-item">
              <a href="#">
                <svg
                  className="icon icon-xxs"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/dashboard">Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Home Layout
            </li>
          </ol>
        </nav>

        <div className="card p-4">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="col-lg-12 mt-2">
                <div
                  className="card-1 skeleton"
                  style={{ height: 54, borderRadius: 10 }}
                ></div>
              </div>
            ))
          ) : (
            <></>
          )}

          <div className={` ${isLoading ? "d-none" : "d-block"}`}>
            <div className="mb-4 bg-light border rounded p-2 d-none">
              <label htmlFor="top_bar" className="text-start d-block">
                Top Bar
              </label>
              <input
                id="top_bar"
                type="text"
                className="form-control top_bar"
                onChange={handleChange}
                name="top_bar"
                value={formData.top_bar}
              />
            </div>

            <div className="accordion mb-2 d-none" id="accordionSlider">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOne">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne"
                    aria-expanded="false"
                    aria-controls="collapseOne"
                  >
                    <svg
                      width={20}
                      fill="currentColor"
                      height={20}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 64 64"
                      className="me-2"
                    >
                      <path d="m31,30.95001v-13.95001h-13.95001c.5,7.46997,6.48004,13.45001,13.95001,13.95001Z" />
                      <path d="m32,33c-6.48999,0-12.14001-3.66003-15-9.02002v23.02002h29.95001c-.52002-7.79999-7.02002-14-14.95001-14Z" />
                      <path d="m33,31.04999c6.06.35999,11.28998,3.89001,14,8.96997v-23.01996h-14v14.04999Z" />
                      <path d="M53 10H11c-.54999 0-1 .45001-1 1v42c0 .54999.45001 1 1 1h42c.54999 0 1-.45001 1-1V11c0-.54999-.45001-1-1-1zm-4 38c0 .54999-.45001 1-1 1H16c-.54999 0-1-.45001-1-1V16c0-.54999.45001-1 1-1h32c.54999 0 1 .45001 1 1v32zM59 14h-3v36h3c.54999 0 1-.45001 1-1V15c0-.54999-.45001-1-1-1zM4 15v34c0 .54999.45001 1 1 1h3V14h-3c-.54999 0-1 .45001-1 1z" />
                    </svg>
                    Home Slider
                  </button>
                </h2>
                <div
                  id="collapseOne"
                  className="accordion-collapse collapse "
                  aria-labelledby="headingOne"
                  data-bs-parent="#accordionSlider"
                >
                  <div className="accordion-body">
                    <div className="row">
                      <label>Left Slide banner ( 1500 X 1373px )</label>
                      <div className="col-md-4 col-6">
                        <div className="input-group imageupload-bx mb-3">
                          <img
                            className="image-preview-banner"
                            src={
                              layout.slider_img !== ""
                                ? layout.slider_img
                                : "/assets/images/placeholder.png"
                            }
                            alt="Preview"
                            id="imageinputBannerTop"
                            data-bs-toggle="modal"
                            data-bs-target="#modal-default"
                          />
                          <input
                            type="hidden"
                            className="form-control imageopen"
                            id="imageinputBannerTopinput"
                            name="productImg[]"
                            defaultValue={
                              layout.slider_img !== ""
                                ? layout.slider_img
                                : "/assets/images/placeholder.png"
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <hr />
                    <label> Slider Images ( 2000 X 582px ) </label>
                    <div id="imageSliderContainer" className="row mt-4">
                      {layout.home_slider && (
                        <>
                          {layout.home_slider.map((image, index) => (
                            <div className="col-md-4 col-6" key={index}>
                              <div
                                className={`input-group imageupload-bx mb-3`}
                              >
                                <img
                                  className="image-preview"
                                  src={image}
                                  alt="Preview"
                                  id={`imageinputN1${index + 1}`}
                                  data-bs-toggle="modal"
                                  data-bs-target="#modal-default"
                                />
                                <input
                                  type="hidden"
                                  className="form-control imageopen"
                                  id={`imageproductN1${index + 1}`}
                                  name="productImg[]"
                                  value={image}
                                />
                              </div>
                              <button
                                className="btn btn-danger d-block m-auto"
                                onClick={removeSliderImageDiv}
                              >
                                Delete
                              </button>
                              <br />
                            </div>
                          ))}
                        </>
                      )}

                      {imageSliderDivs.map((imageSliderDivs, index) => (
                        <React.Fragment key={index}>
                          {imageSliderDivs}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="col-md-12">
                      <button
                        onClick={addSliderImageDiv}
                        className="btn btn-secondary btn-sm"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-1"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1f2937"
                          stroke-width="1.5"
                          stroke-linecap="square"
                          stroke-linejoin="arcs"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M20.4 14.5L16 10 4 20" />
                        </svg>
                        Add Slider
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion mb-2 d-none" id="accordionTrending">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingaccordionTrending">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneTrending"
                    aria-expanded="false"
                    aria-controls="collapseOne"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      enableBackground="new 0 0 1024 1024"
                      viewBox="0 0 1024 1024"
                      className="me-2"
                    >
                      <path d="M490 324.2v219.5c-18-9.6-36.2-19.2-54.4-28.8-45.6-24.1-91.2-48.2-136.9-72.3-6.6-3.5-12.7-7.1-19.7-10.5V212.7c2 1.2 4.7 2.4 7 3.6.1.1.2.1.3.2 6.8 3.6 13.5 7.1 20.3 10.7 2.2 1.2 4.5 2.4 6.7 3.6 2.3 1.2 4.5 2.4 6.8 3.6 4.5 2.4 9.1 4.8 13.6 7.2 45.6 24.1 91.3 48 136.9 72.1.2.1.3 0 .5.1 1.1.6 2.2 1.3 3.3 1.3h0c.2 1 .4.6.5.7 2.6 1.4 5.1 2.9 7.7 4.3 1.5.8 2.8 1.7 4.3 2.5C488 323 489 323.7 490 324.2zM745 213.1v219.2c0 0-.1 0-.1 0-3.2 1.5-5.6 2.9-8.4 4.4-43.5 23-87 45.9-130.5 68.9-.8.4-1.5.9-2.4 1.3-23.2 12.2-46.6 24.2-69.6 36.5V324.2c2-1.5 5.4-2.9 8.2-4.4 43.5-23 87-46 130.5-68.9C696.8 238.2 721 225.8 745 213.1zM720.2 176.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.9 72.3-6.8 3.6-13.6 7.2-20.4 10.8-25.1-13.3-50.2-26.5-75.3-39.8-44.3-23.4-88.5-46.8-132.8-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.2 136.8-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.5 75.4 39.8C631.6 130 675.9 153.4 720.2 176.8zM236 727.4v219.2c-19-9.4-35.9-18.9-53.7-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-14.5-7.3-20.5-10.9V615.8c3 2.1 7.8 4.2 11.8 6.2.6.3 1.1.6 1.6.9 6.8 3.6 13.6 7.4 20.5 11 6.8 3.6 13.6 7 20.4 11h0c45.3 24 90.6 47.7 135.9 71.6.1 0 .2 0 .3 0 .1 0 .2 0 .2.1.2.1.3.1.5.2 1.3.7 2.5 1.3 3.8 2 5.2 2.8 10.3 5.6 15.6 8.4 0 0 0 0 0 0C235.7 727.3 235.8 727.4 236 727.4L236 727.4zM490 616v219.2c-25 13.3-50.3 26.6-75.5 39.9-45.2 23.9-89.5 47.7-135.5 71.6V727.3c0-.2.7-.3 1-.5 2.5-1.3 5-2.6 7.4-3.9 19-10.1 38.1-20.1 57.1-30.2 24.5-12.9 49-25.9 73.4-38.8 9-4.8 18-9.5 27.1-14.2 2.2-1.1 4.3-2.3 6.5-3.4 10.3-5.4 20.5-10.7 30.8-16.2C485 618.7 488 617.4 490 616zM465 579.7L465 579.7c-16 9-33.6 18-50.6 26.9-45.6 24.1-91.1 48.2-136.7 72.4-3.4 1.8-6.7 3.6-10.1 5.4-3.4 1.8-6.8 3.6-10.2 5.4L182.2 650c-44.3-23.4-88.5-46.8-132.7-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.1 136.8-72.2 6.8-3.6 13.6-7.7 20.4-10.7h0c6.3 3 12.5 6.5 18.8 9.8 18.8 10 37.4 19.8 56.2 29.8C376.8 533 421 556.3 465 579.7zM745 727.4v219.2c-19-9.4-35.9-18.9-53.8-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-13.5-7.3-20.5-10.9V615.6c18 9.6 36.2 19.2 54.4 28.9 45.6 24.1 91.2 48.3 136.9 72.4 1.3.7 2.5 1.3 3.8 2C734.4 721.7 739 724.6 745 727.4zM999.1 616.1c-20.4 10.8-40.9 21.4-61.4 32.2-.1.1-.2.1-.3.2-1.5.8-2.9 1.5-4.4 2.3-1.5.8-2.9 1.5-4.3 2.3-.5.3-1.1.6-1.6.8.3-.1.5-.3.8-.4-.1.1-.3.1-.4.2-.1.1-.3.1-.4.2-43.5 23-87 45.9-130.4 68.9-.3.2-.7.3-1 .5-2.3 1.3-4.5 2.5-7.3 3.8 0 0-.1-.1-.1-.1H788v219.6c46-23.8 90.3-47.7 135.5-71.6 25.2-13.3 50.5-26.6 75.5-39.9L999.1 616.1C999 616.1 999.1 616.1 999.1 616.1zM788.3 727.2c2.4-1.3 4.8-2.6 7.3-3.8C793.2 724.7 791.1 726 788.3 727.2zM974.6 579.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.8 72.3-2.5 1.3-5.1 2.7-7.6 4-.1 0-.2.1-.2.1-2.7 1.4-5.4 2.9-8.2 4.4-1.5.8-2.9 1.4-4.3 2.4h0c-25.1-14-50.2-26.7-75.4-40-22.1-11.7-44.3-23.5-66.4-35.2-11.1-5.9-22.1-11.7-33.2-17.5-2.8-1.5-5.5-2.9-8.3-4.4-8.3-4.4-16.5-8.8-24.8-13.2 16.9-8.9 33.9-17.9 50.8-26.8 45.6-24.1 91.2-48.2 136.9-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.6 75.3 39.8C886.1 533 930.3 556.4 974.6 579.8z" />
                    </svg>
                    Choose main categories
                  </button>
                </h2>
                <div
                  id="collapseOneTrending"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingaccordionTrending"
                  data-bs-parent="#headingaccordionTrending"
                >
                  <div className="accordion-body">
                    <div className="col-md-12">
                      <label htmlFor="title">All categories</label>

                      <div className="mb-4 d-none">
                        <label htmlFor="title">Choose Trending products</label>

                        <select
                          multiple="true"
                          placeholder="Select Trending products"
                          id="TrendingProductsSelect"
                          data-allow-clear={1}
                        >
                          {Products ? (
                            Products.map((product) => (
                              <option
                                key={product.id}
                                value={product._id}
                                selected={
                                  !isLoading &&
                                  layout?.trending_product?.includes(
                                    product._id
                                  )
                                    ? "selected"
                                    : null
                                }
                              >
                                {product.title}
                              </option>
                            ))
                          ) : (
                            <>loading....</>
                          )}
                        </select>
                      </div>
                    </div>

                    <div id="imageTrendingBannerContainer" className="row mt-4">
                      {layout.trending_product_banner && (
                        <>
                          {layout.trending_product_banner.map(
                            (banner, index) => (
                              <div className="col-md-3 col-6" key={index}>
                                <div className="imageTbannerDivs-box">
                                  <div
                                    className={`input-group imageupload-bx mb-3`}
                                  >
                                    <img
                                      className="image-preview"
                                      src={banner.imageInput}
                                      alt="Preview"
                                      id={`imageinputN${index + 1}`}
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-default"
                                    />
                                    <input
                                      type="hidden"
                                      className="form-control imageopen"
                                      id={`imageproductN${index + 1}`}
                                      name="productImg[]"
                                      value={banner.imageInput || ""}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="Category"
                                      className="text-center d-block"
                                    >
                                      Category Name
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control productTbannerTIT"
                                      id={`productTbannerTITD${index + 1}`}
                                      name="productTbannerTIT[]"
                                      defaultValue={banner.imageTITInput || ""}
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="title"
                                      className="text-center d-block"
                                    >
                                      Image Url
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control productTbannerUrl"
                                      id={`productTbannerUrlD${index + 1}`}
                                      name="productTbannerUrl[]"
                                      defaultValue={banner.imageUrlInput || ""}
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>
                                </div>

                                <button
                                  className="btn btn-danger d-block m-auto"
                                  onClick={removeSliderImageDiv}
                                >
                                  Delete
                                </button>
                                <br />
                              </div>
                            )
                          )}
                        </>
                      )}

                      {imageTbannerDivs.map((imageTbannerDivs, index) => (
                        <React.Fragment key={index}>
                          {imageTbannerDivs}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="col-md-12">
                      <button
                        onClick={addTbannerImageDiv}
                        className="btn btn-secondary btn-sm"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-1"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1f2937"
                          stroke-width="1.5"
                          stroke-linecap="square"
                          stroke-linejoin="arcs"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M20.4 14.5L16 10 4 20" />
                        </svg>
                        Add Banner (1500 × 500 px)
                      </button>
                    </div>

                    <hr />

                    <label htmlFor="title">Full Category Banner Images</label>

                    <div
                      id="imageTrendingCarousalContainer"
                      className="row mt-4"
                    >
                      {layout.trending_product_carousal && (
                        <>
                          {layout.trending_product_carousal.map(
                            (carousal, index) => (
                              <div className="col-md-4 col-6" key={index}>
                                <div className="imageTcarousalDivs-box">
                                  <div
                                    className={`input-group imageupload-bx mb-3`}
                                  >
                                    <img
                                      className="image-preview"
                                      src={carousal.imageInput}
                                      alt="Preview"
                                      id={`productTcarousalUrlO${index + 1}`}
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-default"
                                    />
                                    <input
                                      type="hidden"
                                      className="form-control imageopen"
                                      id={`productTcarousalUrlI${index + 1}`}
                                      name="productImg[]"
                                      value={carousal.imageInput}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="title"
                                      className="text-center d-block"
                                    >
                                      Image Url
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control imageTcarousalDivs"
                                      id={`productTcarousalUrlD${index + 1}`}
                                      name="productTcarousalUrl[]"
                                      defaultValue={
                                        carousal.imageUrlInput || ""
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>
                                </div>

                                <button
                                  className="btn btn-danger d-block m-auto"
                                  onClick={removeSliderImageDiv}
                                >
                                  Delete
                                </button>
                                <br />
                              </div>
                            )
                          )}
                        </>
                      )}

                      {imageTcarousalDivs.map((imageTcarousalDivs, index) => (
                        <React.Fragment key={index}>
                          {imageTcarousalDivs}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="col-md-12">
                      <button
                        onClick={addTcarousalImageDiv}
                        className="btn btn-secondary btn-sm"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-1"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1f2937"
                          stroke-width="1.5"
                          stroke-linecap="square"
                          stroke-linejoin="arcs"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M20.4 14.5L16 10 4 20" />
                        </svg>
                        Add Carousal (1000 × 1000 px)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion mb-2 d-none" id="accordionSelling">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOneSelling">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneSelling"
                    aria-expanded="false"
                    aria-controls="collapseOneSelling"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      enableBackground="new 0 0 1024 1024"
                      viewBox="0 0 1024 1024"
                      className="me-2"
                    >
                      <path d="M490 324.2v219.5c-18-9.6-36.2-19.2-54.4-28.8-45.6-24.1-91.2-48.2-136.9-72.3-6.6-3.5-12.7-7.1-19.7-10.5V212.7c2 1.2 4.7 2.4 7 3.6.1.1.2.1.3.2 6.8 3.6 13.5 7.1 20.3 10.7 2.2 1.2 4.5 2.4 6.7 3.6 2.3 1.2 4.5 2.4 6.8 3.6 4.5 2.4 9.1 4.8 13.6 7.2 45.6 24.1 91.3 48 136.9 72.1.2.1.3 0 .5.1 1.1.6 2.2 1.3 3.3 1.3h0c.2 1 .4.6.5.7 2.6 1.4 5.1 2.9 7.7 4.3 1.5.8 2.8 1.7 4.3 2.5C488 323 489 323.7 490 324.2zM745 213.1v219.2c0 0-.1 0-.1 0-3.2 1.5-5.6 2.9-8.4 4.4-43.5 23-87 45.9-130.5 68.9-.8.4-1.5.9-2.4 1.3-23.2 12.2-46.6 24.2-69.6 36.5V324.2c2-1.5 5.4-2.9 8.2-4.4 43.5-23 87-46 130.5-68.9C696.8 238.2 721 225.8 745 213.1zM720.2 176.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.9 72.3-6.8 3.6-13.6 7.2-20.4 10.8-25.1-13.3-50.2-26.5-75.3-39.8-44.3-23.4-88.5-46.8-132.8-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.2 136.8-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.5 75.4 39.8C631.6 130 675.9 153.4 720.2 176.8zM236 727.4v219.2c-19-9.4-35.9-18.9-53.7-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-14.5-7.3-20.5-10.9V615.8c3 2.1 7.8 4.2 11.8 6.2.6.3 1.1.6 1.6.9 6.8 3.6 13.6 7.4 20.5 11 6.8 3.6 13.6 7 20.4 11h0c45.3 24 90.6 47.7 135.9 71.6.1 0 .2 0 .3 0 .1 0 .2 0 .2.1.2.1.3.1.5.2 1.3.7 2.5 1.3 3.8 2 5.2 2.8 10.3 5.6 15.6 8.4 0 0 0 0 0 0C235.7 727.3 235.8 727.4 236 727.4L236 727.4zM490 616v219.2c-25 13.3-50.3 26.6-75.5 39.9-45.2 23.9-89.5 47.7-135.5 71.6V727.3c0-.2.7-.3 1-.5 2.5-1.3 5-2.6 7.4-3.9 19-10.1 38.1-20.1 57.1-30.2 24.5-12.9 49-25.9 73.4-38.8 9-4.8 18-9.5 27.1-14.2 2.2-1.1 4.3-2.3 6.5-3.4 10.3-5.4 20.5-10.7 30.8-16.2C485 618.7 488 617.4 490 616zM465 579.7L465 579.7c-16 9-33.6 18-50.6 26.9-45.6 24.1-91.1 48.2-136.7 72.4-3.4 1.8-6.7 3.6-10.1 5.4-3.4 1.8-6.8 3.6-10.2 5.4L182.2 650c-44.3-23.4-88.5-46.8-132.7-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.1 136.8-72.2 6.8-3.6 13.6-7.7 20.4-10.7h0c6.3 3 12.5 6.5 18.8 9.8 18.8 10 37.4 19.8 56.2 29.8C376.8 533 421 556.3 465 579.7zM745 727.4v219.2c-19-9.4-35.9-18.9-53.8-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-13.5-7.3-20.5-10.9V615.6c18 9.6 36.2 19.2 54.4 28.9 45.6 24.1 91.2 48.3 136.9 72.4 1.3.7 2.5 1.3 3.8 2C734.4 721.7 739 724.6 745 727.4zM999.1 616.1c-20.4 10.8-40.9 21.4-61.4 32.2-.1.1-.2.1-.3.2-1.5.8-2.9 1.5-4.4 2.3-1.5.8-2.9 1.5-4.3 2.3-.5.3-1.1.6-1.6.8.3-.1.5-.3.8-.4-.1.1-.3.1-.4.2-.1.1-.3.1-.4.2-43.5 23-87 45.9-130.4 68.9-.3.2-.7.3-1 .5-2.3 1.3-4.5 2.5-7.3 3.8 0 0-.1-.1-.1-.1H788v219.6c46-23.8 90.3-47.7 135.5-71.6 25.2-13.3 50.5-26.6 75.5-39.9L999.1 616.1C999 616.1 999.1 616.1 999.1 616.1zM788.3 727.2c2.4-1.3 4.8-2.6 7.3-3.8C793.2 724.7 791.1 726 788.3 727.2zM974.6 579.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.8 72.3-2.5 1.3-5.1 2.7-7.6 4-.1 0-.2.1-.2.1-2.7 1.4-5.4 2.9-8.2 4.4-1.5.8-2.9 1.4-4.3 2.4h0c-25.1-14-50.2-26.7-75.4-40-22.1-11.7-44.3-23.5-66.4-35.2-11.1-5.9-22.1-11.7-33.2-17.5-2.8-1.5-5.5-2.9-8.3-4.4-8.3-4.4-16.5-8.8-24.8-13.2 16.9-8.9 33.9-17.9 50.8-26.8 45.6-24.1 91.2-48.2 136.9-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.6 75.3 39.8C886.1 533 930.3 556.4 974.6 579.8z" />
                    </svg>
                    Best Selling Laptop
                  </button>
                </h2>
                <div
                  id="collapseOneSelling"
                  className="accordion-collapse collapse "
                  aria-labelledby="headingOneSelling"
                  data-bs-parent="#accordionSelling"
                >
                  <div className="accordion-body">
                    <div className="mb-4">
                      <label htmlFor="title">Choose Best Selling Laptop</label>

                      <select
                        multiple="true"
                        placeholder="Select Best Selling Laptop"
                        id="BestSellingLaptopSelect"
                        data-allow-clear={1}
                      >
                        {layout.best_selling_laptop &&
                          Products &&
                          Products.map((product) => (
                            <option
                              key={product.id}
                              value={product._id}
                              selected={
                                layout.best_selling_laptop.includes(product._id)
                                  ? true
                                  : false
                              }
                            >
                              {product.title}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion mb-2 d-none" id="accordionLatest">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingaccordionLatest">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneLatest"
                    aria-expanded="false"
                    aria-controls="collapseOne"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      enableBackground="new 0 0 1024 1024"
                      viewBox="0 0 1024 1024"
                      className="me-2"
                    >
                      <path d="M490 324.2v219.5c-18-9.6-36.2-19.2-54.4-28.8-45.6-24.1-91.2-48.2-136.9-72.3-6.6-3.5-12.7-7.1-19.7-10.5V212.7c2 1.2 4.7 2.4 7 3.6.1.1.2.1.3.2 6.8 3.6 13.5 7.1 20.3 10.7 2.2 1.2 4.5 2.4 6.7 3.6 2.3 1.2 4.5 2.4 6.8 3.6 4.5 2.4 9.1 4.8 13.6 7.2 45.6 24.1 91.3 48 136.9 72.1.2.1.3 0 .5.1 1.1.6 2.2 1.3 3.3 1.3h0c.2 1 .4.6.5.7 2.6 1.4 5.1 2.9 7.7 4.3 1.5.8 2.8 1.7 4.3 2.5C488 323 489 323.7 490 324.2zM745 213.1v219.2c0 0-.1 0-.1 0-3.2 1.5-5.6 2.9-8.4 4.4-43.5 23-87 45.9-130.5 68.9-.8.4-1.5.9-2.4 1.3-23.2 12.2-46.6 24.2-69.6 36.5V324.2c2-1.5 5.4-2.9 8.2-4.4 43.5-23 87-46 130.5-68.9C696.8 238.2 721 225.8 745 213.1zM720.2 176.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.9 72.3-6.8 3.6-13.6 7.2-20.4 10.8-25.1-13.3-50.2-26.5-75.3-39.8-44.3-23.4-88.5-46.8-132.8-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.2 136.8-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.5 75.4 39.8C631.6 130 675.9 153.4 720.2 176.8zM236 727.4v219.2c-19-9.4-35.9-18.9-53.7-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-14.5-7.3-20.5-10.9V615.8c3 2.1 7.8 4.2 11.8 6.2.6.3 1.1.6 1.6.9 6.8 3.6 13.6 7.4 20.5 11 6.8 3.6 13.6 7 20.4 11h0c45.3 24 90.6 47.7 135.9 71.6.1 0 .2 0 .3 0 .1 0 .2 0 .2.1.2.1.3.1.5.2 1.3.7 2.5 1.3 3.8 2 5.2 2.8 10.3 5.6 15.6 8.4 0 0 0 0 0 0C235.7 727.3 235.8 727.4 236 727.4L236 727.4zM490 616v219.2c-25 13.3-50.3 26.6-75.5 39.9-45.2 23.9-89.5 47.7-135.5 71.6V727.3c0-.2.7-.3 1-.5 2.5-1.3 5-2.6 7.4-3.9 19-10.1 38.1-20.1 57.1-30.2 24.5-12.9 49-25.9 73.4-38.8 9-4.8 18-9.5 27.1-14.2 2.2-1.1 4.3-2.3 6.5-3.4 10.3-5.4 20.5-10.7 30.8-16.2C485 618.7 488 617.4 490 616zM465 579.7L465 579.7c-16 9-33.6 18-50.6 26.9-45.6 24.1-91.1 48.2-136.7 72.4-3.4 1.8-6.7 3.6-10.1 5.4-3.4 1.8-6.8 3.6-10.2 5.4L182.2 650c-44.3-23.4-88.5-46.8-132.7-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.1 136.8-72.2 6.8-3.6 13.6-7.7 20.4-10.7h0c6.3 3 12.5 6.5 18.8 9.8 18.8 10 37.4 19.8 56.2 29.8C376.8 533 421 556.3 465 579.7zM745 727.4v219.2c-19-9.4-35.9-18.9-53.8-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-13.5-7.3-20.5-10.9V615.6c18 9.6 36.2 19.2 54.4 28.9 45.6 24.1 91.2 48.3 136.9 72.4 1.3.7 2.5 1.3 3.8 2C734.4 721.7 739 724.6 745 727.4zM999.1 616.1c-20.4 10.8-40.9 21.4-61.4 32.2-.1.1-.2.1-.3.2-1.5.8-2.9 1.5-4.4 2.3-1.5.8-2.9 1.5-4.3 2.3-.5.3-1.1.6-1.6.8.3-.1.5-.3.8-.4-.1.1-.3.1-.4.2-.1.1-.3.1-.4.2-43.5 23-87 45.9-130.4 68.9-.3.2-.7.3-1 .5-2.3 1.3-4.5 2.5-7.3 3.8 0 0-.1-.1-.1-.1H788v219.6c46-23.8 90.3-47.7 135.5-71.6 25.2-13.3 50.5-26.6 75.5-39.9L999.1 616.1C999 616.1 999.1 616.1 999.1 616.1zM788.3 727.2c2.4-1.3 4.8-2.6 7.3-3.8C793.2 724.7 791.1 726 788.3 727.2zM974.6 579.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.8 72.3-2.5 1.3-5.1 2.7-7.6 4-.1 0-.2.1-.2.1-2.7 1.4-5.4 2.9-8.2 4.4-1.5.8-2.9 1.4-4.3 2.4h0c-25.1-14-50.2-26.7-75.4-40-22.1-11.7-44.3-23.5-66.4-35.2-11.1-5.9-22.1-11.7-33.2-17.5-2.8-1.5-5.5-2.9-8.3-4.4-8.3-4.4-16.5-8.8-24.8-13.2 16.9-8.9 33.9-17.9 50.8-26.8 45.6-24.1 91.2-48.2 136.9-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.6 75.3 39.8C886.1 533 930.3 556.4 974.6 579.8z" />
                    </svg>
                    Category Service Section
                  </button>
                </h2>
                <div
                  id="collapseOneLatest"
                  className="accordion-collapse collapse "
                  aria-labelledby="headingaccordionLatest"
                  data-bs-parent="#headingaccordionLatest"
                >
                  <div className="accordion-body">
                    <div className="col-md-12">
                      <div className="mb-4 d-none">
                        <label htmlFor="title"> Choose Latest products</label>
                        <select
                          multiple="true"
                          placeholder="Choose Latest products"
                          id="LatestProductSelect"
                          data-allow-clear={1}
                        >
                          {Products ? (
                            Products.map((product) => (
                              <option
                                key={product.id}
                                value={product._id}
                                selected={
                                  !isLoading &&
                                  layout?.latest_product?.includes(product._id)
                                    ? "selected"
                                    : null
                                }
                              >
                                {product.title}
                              </option>
                            ))
                          ) : (
                            <>loading....</>
                          )}
                        </select>
                      </div>
                    </div>
                    <label>Four Section Blurb</label>

                    <div
                      id="imageTrendingBanner2Container"
                      className="row mt-4"
                    >
                      {layout.latest_product_banner && (
                        <>
                          {layout.latest_product_banner.map((banner, index) => (
                            <div className="col-md-3 col-6" key={index}>
                              <div className="imageTbanner2Divs-box">
                                <div
                                  className={`input-group imageupload-bx mb-3`}
                                >
                                  <img
                                    className="image-preview"
                                    src={banner.imageInput}
                                    alt="Preview"
                                    id={`imageinputTbanner2img${index + 1}`}
                                    data-bs-toggle="modal"
                                    data-bs-target="#modal-default"
                                  />
                                  <input
                                    type="hidden"
                                    className="form-control imageopen"
                                    id={`imageinputImageTbanner2Url${
                                      index + 1
                                    }`}
                                    name="productImg[]"
                                    value={banner.imageInput}
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="title"
                                    className="text-center d-block"
                                  >
                                    Image Title
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control productTbanner2TIT"
                                    id={`productTbanner2TITD${index + 1}`}
                                    name="productTbanner2TIT[][]"
                                    defaultValue={banner.imageTITInput || ""}
                                    onChange={(e) => {
                                      const value = e.target.value; // Get the value directly
                                      console.log(value); // Do something with the value if needed
                                    }}
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="title"
                                    className="text-center d-block"
                                  >
                                    Image Paragraph
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control productTbanner2Para"
                                    id={`productTbanner2ParaD${index + 1}`}
                                    name="productTbanner2Para[][]"
                                    defaultValue={banner.imageParaInput || ""}
                                    onChange={(e) => {
                                      const value = e.target.value; // Get the value directly
                                      console.log(value); // Do something with the value if needed
                                    }}
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="title"
                                    className="text-center d-block"
                                  >
                                    Image Url
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control productTbanner2Url"
                                    id={`productTbanner2UrlD${index + 1}`}
                                    name="productTbanner2Url[][]"
                                    defaultValue={banner.imageUrlInput || ""}
                                    onChange={(e) => {
                                      const value = e.target.value; // Get the value directly
                                      console.log(value); // Do something with the value if needed
                                    }}
                                  />
                                </div>
                              </div>

                              <button
                                className="btn btn-danger d-block m-auto"
                                onClick={removeSliderImageDiv}
                              >
                                Delete
                              </button>
                              <br />
                            </div>
                          ))}
                        </>
                      )}

                      {imageTbanner2Divs.map((imageTbanner2Divs, index) => (
                        <React.Fragment key={index}>
                          {imageTbanner2Divs}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="col-md-12">
                      <button
                        onClick={addTbanner2ImageDiv}
                        className="btn btn-secondary btn-sm"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-1"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1f2937"
                          stroke-width="1.5"
                          stroke-linecap="square"
                          stroke-linejoin="arcs"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M20.4 14.5L16 10 4 20" />
                        </svg>
                        Add Banner (1500 × 500 px)
                      </button>
                    </div>

                    <hr />

                    <label>Service Category</label>

                    <div
                      id="imageTrendingCarousal2Container"
                      className="row mt-4"
                    >
                      {layout.latest_product_carousal && (
                        <>
                          {layout.latest_product_carousal.map(
                            (carousal, index) => (
                              <div className="col-md-3 col-6" key={index}>
                                <div className="imageTcarousal2Divs-box">
                                  <div
                                    className={`input-group imageupload-bx mb-3`}
                                  >
                                    <img
                                      className="image-preview"
                                      src={carousal.imageInput}
                                      alt="Preview"
                                      id={`imageTcarousal2inputSImgD${
                                        index + 1
                                      }`}
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-default"
                                    />
                                    <input
                                      type="hidden"
                                      className="form-control imageopen"
                                      id={`imageTcarousal2inputImage${
                                        index + 1
                                      }`}
                                      name="productImg[]"
                                      value={carousal.imageInput}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="title"
                                      className="text-center d-block"
                                    >
                                      Image Title
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control productTcarousal2TIT"
                                      id={`productTcarousal2TITD${index + 1}`}
                                      name="productTcarousal2TIT[][]"
                                      defaultValue={
                                        carousal.imageTITInput || ""
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="title"
                                      className="text-center d-block"
                                    >
                                      Image Url
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control productTcarousal2Url"
                                      id={`productTcarousal2UrlD${index + 1}`}
                                      name="productTcarousal2Url[][]"
                                      defaultValue={
                                        carousal.imageUrlInput || ""
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>
                                </div>

                                <button
                                  className="btn btn-danger d-block m-auto"
                                  onClick={removeSliderImageDiv}
                                >
                                  Delete
                                </button>
                                <br />
                              </div>
                            )
                          )}
                        </>
                      )}

                      {imageTcarousal2Divs.map((imageTcarousal2Divs, index) => (
                        <React.Fragment key={index}>
                          {imageTcarousal2Divs}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="col-md-12">
                      <button
                        onClick={addTcarousal2ImageDiv}
                        className="btn btn-secondary btn-sm"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-1"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1f2937"
                          stroke-width="1.5"
                          stroke-linecap="square"
                          stroke-linejoin="arcs"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M20.4 14.5L16 10 4 20" />
                        </svg>
                        Add Carousal (1000 × 1000 px)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion mb-2 d-none" id="accordionBox">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOneBox">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneBox"
                    aria-expanded="false"
                    aria-controls="collapseOneBox"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x={3} y={3} width={18} height={18} rx={2} />
                      <path d="M15 3v18" />
                    </svg>
                    Collection Box
                  </button>
                </h2>
                <div
                  id="collapseOneBox"
                  className="accordion-collapse collapse"
                  aria-labelledby="collapseOneBox"
                  data-bs-parent="#accordionBox"
                >
                  <div className="accordion-body">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-4">
                          <label htmlFor="title">Collection Heading</label>
                          <input
                            type="text"
                            className="form-control"
                            id="collection_heading"
                            name="collection_heading"
                            value={formData.collection_heading || ""}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="title">Collection Paragraph</label>
                          <input
                            type="text"
                            className="form-control"
                            id="collection_paragraph"
                            name="collection_paragraph"
                            value={formData.collection_paragraph || ""}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="title">Collection Url</label>
                          <input
                            type="text"
                            className="form-control"
                            id="collection_url"
                            name="collection_url"
                            value={formData.collection_url || ""}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="col-12">
                          <label htmlFor="title">
                            Collection Image (894 × 678 px )
                          </label>
                          <div className="input-group imageupload-bx mb-3">
                            <img
                              className="image-preview"
                              src={layout.collection_img}
                              alt="Preview"
                              id="imageinputCollectionimg"
                              data-bs-toggle="modal"
                              data-bs-target="#modal-default"
                            />
                            <input
                              type="hidden"
                              className="form-control imageopen"
                              id="imageinputCollectionimg"
                              name="productCollectionimg"
                              value={layout.collection_img}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-muted small mb-3">
              These fields control every visible section of the public home page. Leave a field blank to use the frontend fallback.
            </p>
            <div className="accordion mb-2" id="publicHomeSections">
              {PUBLIC_HOME_FIELD_GROUPS.map((group, index) => (
                <div className="accordion-item mb-2" key={group.title}>
                  <h2 className="accordion-header" id={`headingPublicHomeSection${index}`}>
                    <button
                      className={`accordion-button ${index === 0 ? "" : "collapsed"}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapsePublicHomeSection${index}`}
                      aria-expanded={index === 0 ? "true" : "false"}
                      aria-controls={`collapsePublicHomeSection${index}`}
                    >
                      {group.title}
                    </button>
                  </h2>
                  <div
                    id={`collapsePublicHomeSection${index}`}
                    className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                    aria-labelledby={`headingPublicHomeSection${index}`}
                    data-bs-parent="#publicHomeSections"
                  >
                    <div className="accordion-body">
                      <p className="text-muted small mb-3">{group.help}</p>
                        <div className="row">
                          {group.fields.map((field) => (
                            <div className={field.textarea ? "col-md-6" : "col-md-4"} key={field.name}>
                              <div className="mb-4">
                                <label htmlFor={field.name}>{field.label}</label>
                                {field.textarea ? (
                                  <textarea
                                    className="form-control"
                                    id={field.name}
                                    name={field.name}
                                    rows={field.name === "faq_items_text" || field.name === "testimonial_items_text" ? 6 : 3}
                                    value={formData[field.name] || ""}
                                    onChange={handleChange}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control"
                                    id={field.name}
                                    name={field.name}
                                    value={formData[field.name] || ""}
                                    onChange={handleChange}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {group.title === "Background Images" && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">Home Background Images</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setBackgroundImageItems((items) => [...items, emptyBackgroundImageItem()])}
                              >
                                Add Background Image
                              </button>
                            </div>
                            {backgroundImageItems.map((item, index) => (
                              <div className="border rounded p-3 mb-3" key={`background-image-${index}`}>
                                <div className="row align-items-end">
                                  <div className="col-md-3 mb-3">
                                    <label>Preview</label>
                                    <div
                                      className="border rounded bg-light d-flex align-items-center justify-content-center"
                                      style={{ height: 120, padding: 10 }}
                                    >
                                      {item.imageInput ? (
                                        <img
                                          src={item.imageInput}
                                          alt={item.title || `Background ${index + 1}`}
                                          style={{ width: "100%", height: 96, objectFit: "cover", borderRadius: 4 }}
                                        />
                                      ) : (
                                        <span className="text-muted small">No image</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-md-6 mb-3">
                                    <label>Background Image URL</label>
                                    <input
                                      className="form-control"
                                      value={item.imageInput}
                                      placeholder="/assets/img/background.jpg or https://..."
                                      onChange={(e) => updateArrayItem(setBackgroundImageItems, index, "imageInput", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Alt / Title</label>
                                    <input
                                      className="form-control"
                                      value={item.title}
                                      placeholder="Background title"
                                      onChange={(e) => updateArrayItem(setBackgroundImageItems, index, "title", e.target.value)}
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeArrayItem(setBackgroundImageItems, index, emptyBackgroundImageItem)}
                                >
                                  Remove Background Image
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {group.title === "Stats And Download Banner" && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">Stats</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setStatsItems((items) => [...items, emptyStatsItem()])}
                              >
                                Add Stat
                              </button>
                            </div>
                            {statsItems.map((item, index) => (
                              <div className="border rounded p-3 mb-3" key={`stat-${index}`}>
                                <div className="row">
                                  <div className="col-md-4 mb-3">
                                    <label>Icon Class</label>
                                    <input
                                      className="form-control"
                                      value={item.icon}
                                      onChange={(e) => updateArrayItem(setStatsItems, index, "icon", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-4 mb-3">
                                    <label>Value</label>
                                    <input
                                      className="form-control"
                                      value={item.value}
                                      onChange={(e) => updateArrayItem(setStatsItems, index, "value", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-4 mb-3">
                                    <label>Label</label>
                                    <input
                                      className="form-control"
                                      value={item.label}
                                      onChange={(e) => updateArrayItem(setStatsItems, index, "label", e.target.value)}
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeArrayItem(setStatsItems, index, emptyStatsItem)}
                                >
                                  Remove Stat
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {group.title === "Marquee Logos" && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">Logo Marquee Items</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setLogoMarqueeItems((items) => [...items, emptyLogoMarqueeItem()])}
                              >
                                Add Logo
                              </button>
                            </div>
                            {logoMarqueeItems.map((item, index) => (
                              <div className="border rounded p-3 mb-3" key={`logo-marquee-${index}`}>
                                <div className="row align-items-end">
                                  <div className="col-md-2 mb-3">
                                    <label>Preview</label>
                                    <div
                                      className="border rounded bg-light d-flex align-items-center justify-content-center"
                                      style={{ height: 82, padding: 10 }}
                                    >
                                      {item.imageInput ? (
                                        <img
                                          src={item.imageInput}
                                          alt={item.title || "Logo preview"}
                                          style={{ maxWidth: "100%", maxHeight: 58, objectFit: "contain" }}
                                        />
                                      ) : (
                                        <span className="text-muted small">No logo</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-md-4 mb-3">
                                    <label>Logo Image URL</label>
                                    <input
                                      className="form-control"
                                      value={item.imageInput}
                                      placeholder="/assets/img/logo.png or https://..."
                                      onChange={(e) => updateArrayItem(setLogoMarqueeItems, index, "imageInput", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Logo Link</label>
                                    <input
                                      className="form-control"
                                      value={item.imageUrlInput}
                                      placeholder="/jobs or https://company.com"
                                      onChange={(e) => updateArrayItem(setLogoMarqueeItems, index, "imageUrlInput", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Alt / Title</label>
                                    <input
                                      className="form-control"
                                      value={item.title}
                                      placeholder="Company name"
                                      onChange={(e) => updateArrayItem(setLogoMarqueeItems, index, "title", e.target.value)}
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeArrayItem(setLogoMarqueeItems, index, emptyLogoMarqueeItem)}
                                >
                                  Remove Logo
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {group.title === "Job Categories" && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">Job Categories</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setCategoryItems((items) => [...items, emptyCategoryItem()])}
                              >
                                Add Category
                              </button>
                            </div>
                            {categoryItems.map((item, index) => (
                              <div className="border rounded p-3 mb-3" key={`category-${index}`}>
                                <div className="row">
                                  <div className="col-md-3 mb-3">
                                    <label>Icon Class</label>
                                    <input
                                      className="form-control"
                                      value={item.icon}
                                      onChange={(e) => updateArrayItem(setCategoryItems, index, "icon", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Category Name</label>
                                    <input
                                      className="form-control"
                                      value={item.name}
                                      onChange={(e) => updateArrayItem(setCategoryItems, index, "name", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Badge</label>
                                    <input
                                      className="form-control"
                                      value={item.badge}
                                      onChange={(e) => updateArrayItem(setCategoryItems, index, "badge", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3 mb-3">
                                    <label>Link</label>
                                    <input
                                      className="form-control"
                                      value={item.link}
                                      onChange={(e) => updateArrayItem(setCategoryItems, index, "link", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-12 mb-3">
                                    <label>Description</label>
                                    <textarea
                                      className="form-control"
                                      rows={2}
                                      value={item.description}
                                      onChange={(e) => updateArrayItem(setCategoryItems, index, "description", e.target.value)}
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeArrayItem(setCategoryItems, index, emptyCategoryItem)}
                                >
                                  Remove Category
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {group.title === "How It Works And Testimonials" && (
                          <>
                            <div className="mt-2">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="mb-0">How It Works</h6>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setHowStepItems((items) => [...items, emptyHowStepItem()])}
                                >
                                  Add Step
                                </button>
                              </div>
                              {howStepItems.map((item, index) => (
                                <div className="border rounded p-3 mb-3" key={`how-step-${index}`}>
                                  <div className="row">
                                    <div className="col-md-3 mb-3">
                                      <label>Icon Class</label>
                                      <input
                                        className="form-control"
                                        value={item.icon}
                                        onChange={(e) => updateArrayItem(setHowStepItems, index, "icon", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Title</label>
                                      <input
                                        className="form-control"
                                        value={item.title}
                                        onChange={(e) => updateArrayItem(setHowStepItems, index, "title", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Badge Icon</label>
                                      <input
                                        className="form-control"
                                        value={item.badgeIcon}
                                        onChange={(e) => updateArrayItem(setHowStepItems, index, "badgeIcon", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Badge Text</label>
                                      <input
                                        className="form-control"
                                        value={item.badgeText}
                                        onChange={(e) => updateArrayItem(setHowStepItems, index, "badgeText", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-12 mb-3">
                                      <label>Description</label>
                                      <textarea
                                        className="form-control"
                                        rows={2}
                                        value={item.description}
                                        onChange={(e) => updateArrayItem(setHowStepItems, index, "description", e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeArrayItem(setHowStepItems, index, emptyHowStepItem)}
                                  >
                                    Remove Step
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="mb-0">Testimonials</h6>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setTestimonialItems((items) => [...items, emptyTestimonialItem()])}
                                >
                                  Add Testimonial
                                </button>
                              </div>
                              {testimonialItems.map((item, index) => (
                                <div className="border rounded p-3 mb-3" key={`testimonial-${index}`}>
                                  <div className="row">
                                    <div className="col-md-3 mb-3">
                                      <label>Name</label>
                                      <input
                                        className="form-control"
                                        value={item.name}
                                        onChange={(e) => updateArrayItem(setTestimonialItems, index, "name", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Role</label>
                                      <input
                                        className="form-control"
                                        value={item.role}
                                        onChange={(e) => updateArrayItem(setTestimonialItems, index, "role", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Company</label>
                                      <input
                                        className="form-control"
                                        value={item.company}
                                        onChange={(e) => updateArrayItem(setTestimonialItems, index, "company", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label>Rating</label>
                                      <input
                                        className="form-control"
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={item.rating}
                                        onChange={(e) => updateArrayItem(setTestimonialItems, index, "rating", e.target.value)}
                                      />
                                    </div>
                                    <div className="col-md-12 mb-3">
                                      <label>Testimonial Text</label>
                                      <textarea
                                        className="form-control"
                                        rows={3}
                                        value={item.text}
                                        onChange={(e) => updateArrayItem(setTestimonialItems, index, "text", e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeArrayItem(setTestimonialItems, index, emptyTestimonialItem)}
                                  >
                                    Remove Testimonial
                                  </button>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        {group.title === "FAQ And Blog" && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">FAQ Options</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setFaqItems((items) => [...items, emptyFaqItem()])}
                              >
                                Add FAQ
                              </button>
                            </div>
                            {faqItems.map((item, index) => (
                              <div className="border rounded p-3 mb-3" key={`faq-${index}`}>
                                <div className="row">
                                  <div className="col-md-6 mb-3">
                                    <label>Question</label>
                                    <input
                                      className="form-control"
                                      value={item.question}
                                      onChange={(e) => updateArrayItem(setFaqItems, index, "question", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-6 mb-3">
                                    <label>Answer</label>
                                    <textarea
                                      className="form-control"
                                      rows={2}
                                      value={item.answer}
                                      onChange={(e) => updateArrayItem(setFaqItems, index, "answer", e.target.value)}
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeArrayItem(setFaqItems, index, emptyFaqItem)}
                                >
                                  Remove FAQ
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="accordion mb-2 d-none" id="headingOneSellingSmart">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOneSellingSmart">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneSellingSmart"
                    aria-expanded="false"
                    aria-controls="collapseOneSellingSmart"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      enableBackground="new 0 0 1024 1024"
                      viewBox="0 0 1024 1024"
                      className="me-2"
                    >
                      <path d="M490 324.2v219.5c-18-9.6-36.2-19.2-54.4-28.8-45.6-24.1-91.2-48.2-136.9-72.3-6.6-3.5-12.7-7.1-19.7-10.5V212.7c2 1.2 4.7 2.4 7 3.6.1.1.2.1.3.2 6.8 3.6 13.5 7.1 20.3 10.7 2.2 1.2 4.5 2.4 6.7 3.6 2.3 1.2 4.5 2.4 6.8 3.6 4.5 2.4 9.1 4.8 13.6 7.2 45.6 24.1 91.3 48 136.9 72.1.2.1.3 0 .5.1 1.1.6 2.2 1.3 3.3 1.3h0c.2 1 .4.6.5.7 2.6 1.4 5.1 2.9 7.7 4.3 1.5.8 2.8 1.7 4.3 2.5C488 323 489 323.7 490 324.2zM745 213.1v219.2c0 0-.1 0-.1 0-3.2 1.5-5.6 2.9-8.4 4.4-43.5 23-87 45.9-130.5 68.9-.8.4-1.5.9-2.4 1.3-23.2 12.2-46.6 24.2-69.6 36.5V324.2c2-1.5 5.4-2.9 8.2-4.4 43.5-23 87-46 130.5-68.9C696.8 238.2 721 225.8 745 213.1zM720.2 176.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.9 72.3-6.8 3.6-13.6 7.2-20.4 10.8-25.1-13.3-50.2-26.5-75.3-39.8-44.3-23.4-88.5-46.8-132.8-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.2 136.8-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.5 75.4 39.8C631.6 130 675.9 153.4 720.2 176.8zM236 727.4v219.2c-19-9.4-35.9-18.9-53.7-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-14.5-7.3-20.5-10.9V615.8c3 2.1 7.8 4.2 11.8 6.2.6.3 1.1.6 1.6.9 6.8 3.6 13.6 7.4 20.5 11 6.8 3.6 13.6 7 20.4 11h0c45.3 24 90.6 47.7 135.9 71.6.1 0 .2 0 .3 0 .1 0 .2 0 .2.1.2.1.3.1.5.2 1.3.7 2.5 1.3 3.8 2 5.2 2.8 10.3 5.6 15.6 8.4 0 0 0 0 0 0C235.7 727.3 235.8 727.4 236 727.4L236 727.4zM490 616v219.2c-25 13.3-50.3 26.6-75.5 39.9-45.2 23.9-89.5 47.7-135.5 71.6V727.3c0-.2.7-.3 1-.5 2.5-1.3 5-2.6 7.4-3.9 19-10.1 38.1-20.1 57.1-30.2 24.5-12.9 49-25.9 73.4-38.8 9-4.8 18-9.5 27.1-14.2 2.2-1.1 4.3-2.3 6.5-3.4 10.3-5.4 20.5-10.7 30.8-16.2C485 618.7 488 617.4 490 616zM465 579.7L465 579.7c-16 9-33.6 18-50.6 26.9-45.6 24.1-91.1 48.2-136.7 72.4-3.4 1.8-6.7 3.6-10.1 5.4-3.4 1.8-6.8 3.6-10.2 5.4L182.2 650c-44.3-23.4-88.5-46.8-132.7-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.1 136.8-72.2 6.8-3.6 13.6-7.7 20.4-10.7h0c6.3 3 12.5 6.5 18.8 9.8 18.8 10 37.4 19.8 56.2 29.8C376.8 533 421 556.3 465 579.7zM745 727.4v219.2c-19-9.4-35.9-18.9-53.8-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-13.5-7.3-20.5-10.9V615.6c18 9.6 36.2 19.2 54.4 28.9 45.6 24.1 91.2 48.3 136.9 72.4 1.3.7 2.5 1.3 3.8 2C734.4 721.7 739 724.6 745 727.4zM999.1 616.1c-20.4 10.8-40.9 21.4-61.4 32.2-.1.1-.2.1-.3.2-1.5.8-2.9 1.5-4.4 2.3-1.5.8-2.9 1.5-4.3 2.3-.5.3-1.1.6-1.6.8.3-.1.5-.3.8-.4-.1.1-.3.1-.4.2-.1.1-.3.1-.4.2-43.5 23-87 45.9-130.4 68.9-.3.2-.7.3-1 .5-2.3 1.3-4.5 2.5-7.3 3.8 0 0-.1-.1-.1-.1H788v219.6c46-23.8 90.3-47.7 135.5-71.6 25.2-13.3 50.5-26.6 75.5-39.9L999.1 616.1C999 616.1 999.1 616.1 999.1 616.1zM788.3 727.2c2.4-1.3 4.8-2.6 7.3-3.8C793.2 724.7 791.1 726 788.3 727.2zM974.6 579.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.8 72.3-2.5 1.3-5.1 2.7-7.6 4-.1 0-.2.1-.2.1-2.7 1.4-5.4 2.9-8.2 4.4-1.5.8-2.9 1.4-4.3 2.4h0c-25.1-14-50.2-26.7-75.4-40-22.1-11.7-44.3-23.5-66.4-35.2-11.1-5.9-22.1-11.7-33.2-17.5-2.8-1.5-5.5-2.9-8.3-4.4-8.3-4.4-16.5-8.8-24.8-13.2 16.9-8.9 33.9-17.9 50.8-26.8 45.6-24.1 91.2-48.2 136.9-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.6 75.3 39.8C886.1 533 930.3 556.4 974.6 579.8z" />
                    </svg>
                    Bottom Section
                  </button>
                </h2>
                <div
                  id="collapseOneSellingSmart"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingOneSellingSmart"
                  data-bs-parent="#accordionSellingSmart"
                >
                  <div className="accordion-body">
                    <div className="d-block">
                      <label htmlFor="title">
                        Banner Under Service Category{" "}
                      </label>

                      <div
                        id="imageTrendingBannerCarousalContainer"
                        className="row mt-4"
                      >
                        {layout.service_banner_images && (
                          <>
                            {layout.service_banner_images.map(
                              (carousal, index) => (
                                <div className="col-md-3 col-6" key={index}>
                                  <div className="imageTcarousalDivs-box">
                                    <div
                                      className={`input-group imageupload-bx mb-3`}
                                    >
                                      <img
                                        className="image-preview"
                                        src={carousal.imageInput}
                                        alt="Preview"
                                        id={`productNcarousalUrlO${index + 1}`}
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-default"
                                      />
                                      <input
                                        type="hidden"
                                        className="form-control imageopen"
                                        id={`productNcarousalUrlI${index + 1}`}
                                        name="productImg[]"
                                        value={carousal.imageInput}
                                      />
                                    </div>

                                    <div className="mb-4">
                                      <label
                                        htmlFor="title"
                                        className="text-center d-block"
                                      >
                                        Image Url
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control imageTcarousalDivs"
                                        id={`productNcarousalUrlD${index + 1}`}
                                        name="productTcarousalUrl[]"
                                        defaultValue={
                                          carousal.imageUrlInput || ""
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value; // Get the value directly
                                          console.log(value); // Do something with the value if needed
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <button
                                    className="btn btn-danger d-block m-auto"
                                    onClick={removeSliderImageDiv}
                                  >
                                    Delete
                                  </button>
                                  <br />
                                </div>
                              )
                            )}
                          </>
                        )}

                        {imageNcarousalDivs.map((imageNcarousalDivs, index) => (
                          <React.Fragment key={index}>
                            {imageNcarousalDivs}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="col-md-12">
                        <button
                          onClick={addNcarousalImageDiv}
                          className="btn btn-secondary btn-sm"
                          type="button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mx-1"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1f2937"
                            stroke-width="1.5"
                            stroke-linecap="square"
                            stroke-linejoin="arcs"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M20.4 14.5L16 10 4 20" />
                          </svg>
                          Add banner
                        </button>
                      </div>
                    </div>

                    <hr />

                    <div className="d-block">
                      <label htmlFor="title">Logos Section </label>

                      <div
                        id="imageTrendingLogoCarousalContainer"
                        className="row mt-4"
                      >
                        {layout.service_logos && (
                          <>
                            {layout.service_logos.map((carousal, index) => (
                              <div className="col-md-3 col-6" key={index}>
                                <div className="imageTcarousalDivs-box">
                                  <div
                                    className={`input-group imageupload-bx mb-3`}
                                  >
                                    <img
                                      className="image-preview"
                                      src={carousal.imageInput}
                                      alt="Preview"
                                      id={`productLNcarousalUrlO${index + 1}`}
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-default"
                                    />
                                    <input
                                      type="hidden"
                                      className="form-control imageopen"
                                      id={`productLNcarousalUrlI${index + 1}`}
                                      name="productImg[]"
                                      value={carousal.imageInput}
                                    />
                                  </div>

                                  <div className="mb-4">
                                    <label
                                      htmlFor="title"
                                      className="text-center d-block"
                                    >
                                      Image Url
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control imageTcarousalDivs"
                                      id={`productNcarousalUrlD${index + 1}`}
                                      name="productTcarousalUrl[]"
                                      defaultValue={
                                        carousal.imageUrlInput || ""
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value; // Get the value directly
                                        console.log(value); // Do something with the value if needed
                                      }}
                                    />
                                  </div>
                                </div>

                                <button
                                  className="btn btn-danger d-block m-auto"
                                  onClick={removeSliderImageDiv}
                                >
                                  Delete
                                </button>
                                <br />
                              </div>
                            ))}
                          </>
                        )}

                        {imageLcarousalDivs.map((imageLcarousalDivs, index) => (
                          <React.Fragment key={index}>
                            {imageLcarousalDivs}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="col-md-12">
                        <button
                          onClick={addLcarousalImageDiv}
                          className="btn btn-secondary btn-sm"
                          type="button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mx-1"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1f2937"
                            stroke-width="1.5"
                            stroke-linecap="square"
                            stroke-linejoin="arcs"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M20.4 14.5L16 10 4 20" />
                          </svg>
                          Add Logo
                        </button>
                      </div>
                    </div>

                    <hr />

                    <div className="d-block">
                      <label htmlFor="title">Bottom Banner Section </label>

                      <div
                        id="imageTrendingBottomBannerCarousalContainer"
                        className="row mt-4"
                      >
                        {layout.service_category_Images && (
                          <>
                            {layout.service_category_Images.map(
                              (carousal, index) => (
                                <div className="col-md-3 col-6" key={index}>
                                  <div className="imageTcarousalDivs-box">
                                    <div
                                      className={`input-group imageupload-bx mb-3`}
                                    >
                                      <img
                                        className="image-preview"
                                        src={carousal.imageInput}
                                        alt="Preview"
                                        id={`productBNcarousalUrlO${index + 1}`}
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-default"
                                      />
                                      <input
                                        type="hidden"
                                        className="form-control imageopen"
                                        id={`productBNcarousalUrlI${index + 1}`}
                                        name="productImg[]"
                                        value={carousal.imageInput}
                                      />
                                    </div>

                                    <div className="mb-4">
                                      <label
                                        htmlFor="title"
                                        className="text-center d-block"
                                      >
                                        Image Url
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control imageTcarousalDivs"
                                        id={`productNcarousalUrlD${index + 1}`}
                                        name="productTcarousalUrl[]"
                                        defaultValue={
                                          carousal.imageUrlInput || ""
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value; // Get the value directly
                                          console.log(value); // Do something with the value if needed
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <button
                                    className="btn btn-danger d-block m-auto"
                                    onClick={removeSliderImageDiv}
                                  >
                                    Delete
                                  </button>
                                  <br />
                                </div>
                              )
                            )}
                          </>
                        )}

                        {imageBBcarousalDivs.map(
                          (imageBBcarousalDivs, index) => (
                            <React.Fragment key={index}>
                              {imageBBcarousalDivs}
                            </React.Fragment>
                          )
                        )}
                      </div>

                      <div className="col-md-12">
                        <button
                          onClick={addBBcarousalImageDiv}
                          className="btn btn-secondary btn-sm"
                          type="button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mx-1"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1f2937"
                            stroke-width="1.5"
                            stroke-linecap="square"
                            stroke-linejoin="arcs"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M20.4 14.5L16 10 4 20" />
                          </svg>
                          Add Banner
                        </button>
                      </div>
                    </div>

                    <div className="mb-4 d-none">
                      <label htmlFor="title">
                        Choose Best Selling Smartphone
                      </label>

                      <select
                        multiple="true"
                        placeholder="Select Best Selling Smartphone"
                        id="BestSellingSmartphoneSelect"
                        data-allow-clear={1}
                      >
                        {Products ? (
                          Products.map((product) => (
                            <option
                              key={product.id}
                              value={product._id}
                              selected={
                                !isLoading &&
                                layout.best_selling_smartphone.includes(
                                  product._id
                                )
                                  ? "selected"
                                  : null
                              }
                            >
                              {product.title}
                            </option>
                          ))
                        ) : (
                          <>loading....</>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr className="mt-3 mb-3" />
            <h5 className="d-none"> Product Section</h5>
            <div className="accordion mb-2 d-none" id="headingOneRecommendedProduct">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingRecommendedProduct">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOneRecommendedProduct"
                    aria-expanded="false"
                    aria-controls="collapseOneRecommendedProduct"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={20}
                      width={20}
                      fill="currentColor"
                      enableBackground="new 0 0 1024 1024"
                      viewBox="0 0 1024 1024"
                      className="me-2"
                    >
                      <path d="M490 324.2v219.5c-18-9.6-36.2-19.2-54.4-28.8-45.6-24.1-91.2-48.2-136.9-72.3-6.6-3.5-12.7-7.1-19.7-10.5V212.7c2 1.2 4.7 2.4 7 3.6.1.1.2.1.3.2 6.8 3.6 13.5 7.1 20.3 10.7 2.2 1.2 4.5 2.4 6.7 3.6 2.3 1.2 4.5 2.4 6.8 3.6 4.5 2.4 9.1 4.8 13.6 7.2 45.6 24.1 91.3 48 136.9 72.1.2.1.3 0 .5.1 1.1.6 2.2 1.3 3.3 1.3h0c.2 1 .4.6.5.7 2.6 1.4 5.1 2.9 7.7 4.3 1.5.8 2.8 1.7 4.3 2.5C488 323 489 323.7 490 324.2zM745 213.1v219.2c0 0-.1 0-.1 0-3.2 1.5-5.6 2.9-8.4 4.4-43.5 23-87 45.9-130.5 68.9-.8.4-1.5.9-2.4 1.3-23.2 12.2-46.6 24.2-69.6 36.5V324.2c2-1.5 5.4-2.9 8.2-4.4 43.5-23 87-46 130.5-68.9C696.8 238.2 721 225.8 745 213.1zM720.2 176.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.9 72.3-6.8 3.6-13.6 7.2-20.4 10.8-25.1-13.3-50.2-26.5-75.3-39.8-44.3-23.4-88.5-46.8-132.8-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.2 136.8-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.5 75.4 39.8C631.6 130 675.9 153.4 720.2 176.8zM236 727.4v219.2c-19-9.4-35.9-18.9-53.7-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-14.5-7.3-20.5-10.9V615.8c3 2.1 7.8 4.2 11.8 6.2.6.3 1.1.6 1.6.9 6.8 3.6 13.6 7.4 20.5 11 6.8 3.6 13.6 7 20.4 11h0c45.3 24 90.6 47.7 135.9 71.6.1 0 .2 0 .3 0 .1 0 .2 0 .2.1.2.1.3.1.5.2 1.3.7 2.5 1.3 3.8 2 5.2 2.8 10.3 5.6 15.6 8.4 0 0 0 0 0 0C235.7 727.3 235.8 727.4 236 727.4L236 727.4zM490 616v219.2c-25 13.3-50.3 26.6-75.5 39.9-45.2 23.9-89.5 47.7-135.5 71.6V727.3c0-.2.7-.3 1-.5 2.5-1.3 5-2.6 7.4-3.9 19-10.1 38.1-20.1 57.1-30.2 24.5-12.9 49-25.9 73.4-38.8 9-4.8 18-9.5 27.1-14.2 2.2-1.1 4.3-2.3 6.5-3.4 10.3-5.4 20.5-10.7 30.8-16.2C485 618.7 488 617.4 490 616zM465 579.7L465 579.7c-16 9-33.6 18-50.6 26.9-45.6 24.1-91.1 48.2-136.7 72.4-3.4 1.8-6.7 3.6-10.1 5.4-3.4 1.8-6.8 3.6-10.2 5.4L182.2 650c-44.3-23.4-88.5-46.8-132.7-70.2 17-9 33.9-17.9 50.9-26.9 45.6-24.1 91.2-48.1 136.8-72.2 6.8-3.6 13.6-7.7 20.4-10.7h0c6.3 3 12.5 6.5 18.8 9.8 18.8 10 37.4 19.8 56.2 29.8C376.8 533 421 556.3 465 579.7zM745 727.4v219.2c-19-9.4-35.9-18.9-53.8-28.3-45.6-24.1-91.1-48.2-136.7-72.2-6.9-3.6-13.5-7.3-20.5-10.9V615.6c18 9.6 36.2 19.2 54.4 28.9 45.6 24.1 91.2 48.3 136.9 72.4 1.3.7 2.5 1.3 3.8 2C734.4 721.7 739 724.6 745 727.4zM999.1 616.1c-20.4 10.8-40.9 21.4-61.4 32.2-.1.1-.2.1-.3.2-1.5.8-2.9 1.5-4.4 2.3-1.5.8-2.9 1.5-4.3 2.3-.5.3-1.1.6-1.6.8.3-.1.5-.3.8-.4-.1.1-.3.1-.4.2-.1.1-.3.1-.4.2-43.5 23-87 45.9-130.4 68.9-.3.2-.7.3-1 .5-2.3 1.3-4.5 2.5-7.3 3.8 0 0-.1-.1-.1-.1H788v219.6c46-23.8 90.3-47.7 135.5-71.6 25.2-13.3 50.5-26.6 75.5-39.9L999.1 616.1C999 616.1 999.1 616.1 999.1 616.1zM788.3 727.2c2.4-1.3 4.8-2.6 7.3-3.8C793.2 724.7 791.1 726 788.3 727.2zM974.6 579.8c-17 9-33.9 17.9-50.9 26.9-45.6 24.1-91.2 48.2-136.8 72.3-2.5 1.3-5.1 2.7-7.6 4-.1 0-.2.1-.2.1-2.7 1.4-5.4 2.9-8.2 4.4-1.5.8-2.9 1.4-4.3 2.4h0c-25.1-14-50.2-26.7-75.4-40-22.1-11.7-44.3-23.5-66.4-35.2-11.1-5.9-22.1-11.7-33.2-17.5-2.8-1.5-5.5-2.9-8.3-4.4-8.3-4.4-16.5-8.8-24.8-13.2 16.9-8.9 33.9-17.9 50.8-26.8 45.6-24.1 91.2-48.2 136.9-72.3 6.8-3.6 13.6-7.2 20.4-10.8 25.1 13.3 50.2 26.6 75.3 39.8C886.1 533 930.3 556.4 974.6 579.8z" />
                    </svg>
                    Recommended Products
                  </button>
                </h2>
                <div
                  id="collapseOneRecommendedProduct"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingOneRecommendedProduct"
                  data-bs-parent="#accordionRecommendedProduct"
                >
                  <div className="accordion-body">
                    <div className="mb-4">
                      <label htmlFor="title">Choose Recommended Products</label>

                      <select
                        multiple="true"
                        placeholder="Select Recommended Products"
                        id="RecommendedProductsSelect"
                        data-allow-clear={1}
                      >
                        {Products ? (
                          Products.map((product) => (
                            <option
                              key={product.id}
                              value={product._id}
                              selected={
                                !isLoading &&
                                layout.recommended_products.includes(
                                  product._id
                                )
                                  ? "selected"
                                  : null
                              }
                            >
                              {product.title}
                            </option>
                          ))
                        ) : (
                          <>loading....</>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              {SubmitLoading ? (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={submitData}
                >
                  Update layout
                </button>
              ) : (
                <button class="btn btn-secondary btn-sm" type="button" disabled>
                  <span class="ms-1">Loading...</span>
                  <span
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </button>
              )}
            </div>
          </div>

          <Gallery />
        </div>
      </main>
    </>
  );
};

export default HomeLayout;
